import { useState, useEffect } from "react";

// Design Version Configuration System
export type DesignVersion = "v1" | "v2";

export interface DesignConfig {
  version: DesignVersion;
  layout: {
    formPosition: "left" | "center" | "right";
    cartPosition: "left" | "center" | "right";
    isVerticalStack: boolean;
    showNumberedSteps: boolean;
  };
  styling: {
    primaryColor: string;
    backgroundColor: string;
    borderRadius: string;
    spacing: string;
  };
  features: {
    addressFetching: boolean;
    flatNoField: boolean;
    numberedSections: boolean;
  };
}

export const designConfigs: Record<DesignVersion, DesignConfig> = {
  v1: {
    version: "v1",
    layout: {
      formPosition: "left",
      cartPosition: "right",
      isVerticalStack: false,
      showNumberedSteps: false,
    },
    styling: {
      primaryColor: "#023b8f",
      backgroundColor: "#F2F9FF",
      borderRadius: "8px",
      spacing: "16px",
    },
    features: {
      addressFetching: false,
      flatNoField: false,
      numberedSections: false,
    },
  },
  v2: {
    version: "v2",
    layout: {
      formPosition: "left",
      cartPosition: "right",
      isVerticalStack: false,
      showNumberedSteps: true,
    },
    styling: {
      primaryColor: "#2563EB",
      backgroundColor: "rgb(247, 251, 253)",
      borderRadius: "12px",
      spacing: "20px",
    },
    features: {
      addressFetching: true,
      flatNoField: true,
      numberedSections: true,
    },
  },
};

export class DesignVersionManager {
  private static currentVersion: DesignVersion = "v2";

  static getCurrentVersion(): DesignVersion {
    // Check localStorage first, then URL params, then default to V2
    const savedVersion = localStorage.getItem(
      "nfo-design-version"
    ) as DesignVersion;
    const urlParams = new URLSearchParams(window.location.search);
    const urlVersion = urlParams.get("design_version") as DesignVersion;

    if (urlVersion && this.isValidVersion(urlVersion)) {
      this.setVersion(urlVersion);
      return urlVersion;
    }

    if (savedVersion && this.isValidVersion(savedVersion)) {
      return savedVersion;
    }

    // Default to V2 for better user experience
    return "v2";
  }

  static setVersion(version: DesignVersion): void {
    if (!this.isValidVersion(version)) return;

    // Determine current URL param and stored version
    const url = new URL(window.location.href);
    const currentParam = url.searchParams.get(
      "design_version"
    ) as DesignVersion | null;
    const storedVersion = localStorage.getItem(
      "nfo-design-version"
    ) as DesignVersion | null;

    // If everything already matches, do nothing
    const isSameVersion =
      this.currentVersion === version &&
      currentParam === version &&
      storedVersion === version;

    if (isSameVersion) {
      return;
    }

    // Update internal state and storage
    this.currentVersion = version;
    if (storedVersion !== version) {
      localStorage.setItem("nfo-design-version", version);
    }

    // Only update URL if param actually differs
    if (currentParam !== version) {
      url.searchParams.set("design_version", version);
      window.history.replaceState({}, "", url.toString());
    }
  }

  static getConfig(): DesignConfig {
    return designConfigs[this.getCurrentVersion()];
  }

  static isValidVersion(version: string): version is DesignVersion {
    return version === "v1" || version === "v2";
  }

  static switchVersion(): void {
    const current = this.getCurrentVersion();
    const newVersion = current === "v1" ? "v2" : "v1";
    this.setVersion(newVersion);
    window.location.reload(); // Reload to apply new design
  }
}

// React hook for design version
export const useDesignVersion = () => {
  const [version, setVersion] = useState<DesignVersion>(
    DesignVersionManager.getCurrentVersion()
  );
  const [config, setConfig] = useState<DesignConfig>(
    DesignVersionManager.getConfig()
  );

  useEffect(() => {
    const currentVersion = DesignVersionManager.getCurrentVersion();
    setVersion(currentVersion);
    setConfig(DesignVersionManager.getConfig());
  }, []);

  const switchVersion = () => {
    DesignVersionManager.switchVersion();
  };

  return {
    version,
    config,
    switchVersion,
    setVersion: DesignVersionManager.setVersion,
  };
};
